/*
 * @author        joseph.murphy@zumper.com
 * @copyright     Copyright (c) 2023, Zumper
 * @description   nr file ported over to nr-utils
 */

import { IS_BROWSER, createSettingInterface, getLiveEnv, switchSetting } from '@zcode/settings'
import { Setting } from './constants'

const { createSetting, freeze } = createSettingInterface()

export const zumperMode = getLiveEnv('ZUMPER_MODE')
export const stackName = getLiveEnv('STACK_NAME')
export const ZUMPER_RENTALS_REDIRECTS = getLiveEnv('ZUMPER_RENTALS_REDIRECTS') === 'true'

export const BLDG_BASE_URL = 'apartment-buildings'
export const NZR_BLDG = `/${BLDG_BASE_URL}`
export const NZR_BLDG_SEP = 'apartments-at'

export const MAPBOX_STYLE_KEY = 'mapbox://styles/zumpermaps/cjwv9ajrh01i11cp5bq7mcx1v?optimize=true'
export const MAPBOX_STYLE_KEY_Z4 =
  'mapbox://styles/zumpermaps/ckqwuarar5e4a17rrndqwjoko?optimize=true'
export const MAPBOX_STYLE_KEY_Z4_DARK =
  'mapbox://styles/zumpermaps/cl8kkt15k000814o29wq3gs1o?optimize=true'

export const APTS_BASE_URL = 'apartments-for-rent'
export const APP_HOSTNAME: string = createSetting(
  Setting.APP_HOSTNAME,
  getLiveEnv('APP_HOSTNAME', IS_BROWSER ? window?.location?.hostname : 'localhost')
)
export const CODE_VERSION = createSetting(Setting.CODE_VERSION, getLiveEnv('REACT_APP_VERSION'))
export const CLIENT_LOGGING = createSetting(Setting.CLIENT_LOGGING, {
  LOG_LEVEL: switchSetting(
    getLiveEnv('REACT_APP_LOG_LEVEL_CLIENT_PROD'),
    getLiveEnv('REACT_APP_LOG_LEVEL_CLIENT_TEST'),
    getLiveEnv('REACT_APP_LOG_LEVEL_CLIENT_TEST')
  ),
})
export const DEBUG = createSetting(Setting.DEBUG, zumperMode !== 'prod')
export const IMAGE_STATIC_CDN_URL = createSetting(
  Setting.IMAGE_STATIC_CDN_URL,
  getLiveEnv('REACT_APP_IMAGE_STATIC_CDN_URL')
)
export const IS_LOCAL_DEV_MODE = createSetting(
  Setting.IS_LOCAL_DEV_MODE,
  zumperMode === 'dev' || zumperMode === 'local'
)
export const SERVER_BASIC_AUTH_TOKEN = createSetting(
  Setting.SERVER_BASIC_AUTH_TOKEN,
  getLiveEnv('SERVER_BASIC_AUTH_TOKEN')
)

export const MAPBOX_BROWSER_KEY = createSetting(
  Setting.MAPBOX_BROWSER_KEY,
  getLiveEnv('REACT_APP_MAPBOX_BROWSER_KEY')
)

export const MAPBOX_BROWSER_KEY_Z4 = createSetting(
  Setting.MAPBOX_BROWSER_KEY_Z4,
  getLiveEnv('REACT_APP_MAPBOX_BROWSER_KEY_Z4')
)
export const MAPS_BROWSER_URL_KVP = `key=${MAPBOX_BROWSER_KEY}`

const mediaCDNUrl = getLiveEnv('REACT_APP_MEDIA_CDN_URL')
export const MEDIA_CDN_URL = createSetting(
  Setting.MEDIA_CDN_URL,
  mediaCDNUrl
    ? switchSetting(mediaCDNUrl, mediaCDNUrl.replace('://', `://test-${stackName}-`), mediaCDNUrl)
    : 'https://img.zumpercdn.com'
)
const internalVideoCDNUrl = getLiveEnv('REACT_APP_INTERNAL_VIDEO_CDN_URL')
export const INTERNAL_VIDEO_CDN_URL = createSetting(
  Setting.INTERNAL_VIDEO_CDN_URL,
  internalVideoCDNUrl
    ? switchSetting(
        internalVideoCDNUrl,
        internalVideoCDNUrl.replace('://', `://test-${stackName}-`),
        `${internalVideoCDNUrl}/video`
      )
    : 'https://vid.zumpercdn.com'
)
export const MEDIA_CDN_URLS = createSetting(Setting.MEDIA_CDN_URLS, [MEDIA_CDN_URL])

export const OPTIMIZELY_SDK_KEY = createSetting(
  Setting.OPTIMIZELY_SDK_KEY,
  switchSetting(
    getLiveEnv('REACT_APP_OPTIMIZELY_SDK_KEY_PROD'),
    getLiveEnv('REACT_APP_OPTIMIZELY_SDK_KEY_TEST'),
    getLiveEnv('REACT_APP_OPTIMIZELY_SDK_KEY_LOCAL')
  )
)
export const STATIC_CDN_URL = createSetting(
  Setting.STATIC_CDN_URL,
  getLiveEnv('REACT_APP_STATIC_CDN_URL')
)

export const STATIC_CDN_URLS = createSetting(Setting.STATIC_CDN_URLS, [STATIC_CDN_URL])
export const STORAGE_PREFIX = createSetting(
  Setting.STORAGE_PREFIX,
  getLiveEnv('REACT_APP_STORAGE_PREFIX')
)

export const imageStaticUrl = (path: string) => {
  // @ts-expect-error Operator '>=' cannot be applied to types 'string' and 'number'. ts(2365)
  const p = path >= 0 ? path : String(path).replace(/^\/+/, '')
  return `${IMAGE_STATIC_CDN_URL}/${p}`
}

export const mediaUrl = (path: number | string, mediaSize?: '1280x960' | 'full') => {
  const parts = [MEDIA_CDN_URL, String(path).replace(/^\/+/, '')]
  if (mediaSize) {
    parts.push(mediaSize)
  }
  return parts.join('/')
}

export const staticUrl = (path: string) => {
  // @ts-expect-error Operator '>=' cannot be applied to types 'string' and 'number'. ts(2365)
  const p = path >= 0 ? path : String(path).replace(/^\/+/, '')
  return `${STATIC_CDN_URLS[0]}/${p}`
}

export const APP_PROTOCOL = createSetting(
  Setting.APP_PROTOCOL,
  getLiveEnv('APP_PROTOCOL', 'https:')
)

freeze()
